<template>
  <div class="summary2-wrapper">
    <!-- Animated background particles -->
    <div class="particles">
      <span v-for="n in 20" :key="n" class="particle" :style="particleStyle(n)"></span>
    </div>

    <!-- Header -->
    <header class="summary2-header" :class="{ visible: mounted }">
      <div class="header-line"></div>
      <h1 class="summary2-title">Auction Summary</h1>
      <div class="header-line"></div>
    </header>

    <!-- Stats row -->
    <div class="stats-row" :class="{ visible: mounted }">
      <!-- Bid percentage ring -->
      <div class="stat-card ring-card">
        <svg class="ring-svg" viewBox="0 0 120 120">
          <circle class="ring-bg" cx="60" cy="60" r="52" />
          <circle
            class="ring-fill"
            cx="60" cy="60" r="52"
            :stroke-dasharray="`${circumference} ${circumference}`"
            :stroke-dashoffset="ringOffset"
          />
        </svg>
        <div class="ring-content">
          <span class="ring-value">{{ displayedPercentage }}<span class="ring-unit">%</span></span>
          <span class="ring-label">of guests<br>have placed a bid</span>
        </div>
      </div>

      <!-- Total raised -->
      <div v-if="showTotalRaised" class="stat-card total-card">
        <div class="total-icon">✦</div>
        <div class="total-label">Total Raised</div>
        <div class="total-amount">{{ formattedTotal }}</div>
        <div class="total-sub">combined bids &amp; donations</div>
      </div>

      <!-- Quick numbers -->
      <div class="stat-card numbers-card">
        <div class="number-row">
          <span class="number-val">{{ totalItems }}</span>
          <span class="number-desc">Auction Items</span>
        </div>
        <div class="number-divider"></div>
        <div class="number-row">
          <span class="number-val">{{ itemsWithBids }}</span>
          <span class="number-desc">Items with Bids</span>
        </div>
        <div class="number-divider"></div>
        <div class="number-row">
          <span class="number-val">{{ totalBidCount }}</span>
          <span class="number-desc">Total Bids Placed</span>
        </div>
      </div>
    </div>

    <!-- Leaderboards -->
    <div class="leaderboards" :class="{ visible: mounted }">
      <!-- Most active -->
      <div class="leaderboard-card">
        <h2 class="leaderboard-title">
          <span class="lb-icon">⚡</span> Most Active Tables
          <span class="lb-subtitle">by number of bids placed</span>
        </h2>
        <div class="lb-list">
          <div
            v-for="(table, index) in sortedTopTables.slice(0, 5)"
            :key="table.name"
            class="lb-row"
            :style="{ animationDelay: `${0.1 + index * 0.08}s` }"
          >
            <span class="lb-rank" :class="`rank-${index + 1}`">{{ rankEmoji(index) }}</span>
            <span class="lb-name">{{ table.name }}</span>
            <div class="lb-bar-wrap">
              <div
                class="lb-bar"
                :style="{ width: barWidth(table.bidCount, maxBidCount) }"
              ></div>
            </div>
            <span class="lb-count">{{ table.bidCount }}</span>
          </div>
        </div>
      </div>

      <!-- Leading tables -->
      <div class="leaderboard-card">
        <h2 class="leaderboard-title">
          <span class="lb-icon">🏆</span> Leading in Most Items
        </h2>
        <div class="lb-list">
          <div
            v-for="(table, index) in sortedLeadingTables.slice(0, 5)"
            :key="table.name"
            class="lb-row"
            :style="{ animationDelay: `${0.1 + index * 0.08}s` }"
          >
            <span class="lb-rank" :class="`rank-${index + 1}`">{{ rankEmoji(index) }}</span>
            <span class="lb-name">{{ table.name }}</span>
            <div class="lb-bar-wrap">
              <div
                class="lb-bar lb-bar-gold"
                :style="{ width: barWidth(table.leadingBids, maxLeadingBids) }"
              ></div>
            </div>
            <span class="lb-count">{{ table.leadingBids }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import {
  listenToBidPercentage,
  listenToBidsPerTable,
  listenToTablesWithLeadingBids,
} from "@/services/dataService";
import { db } from "@/firebase";
import { collection, onSnapshot, doc, getDoc } from "firebase/firestore";

const mounted = ref(false);
const showTotalRaised = ref(true);
const bidPercentage = ref(0);
const displayedPercentage = ref(0);
const topTables = ref([]);
const leadingTables = ref([]);
const totalAmount = ref(0);
const totalItems = ref(0);
const itemsWithBids = ref(0);
const totalBidCount = ref(0);

// Exchange rates to SEK (fallback values, overwritten by live fetch)
const fxRates = ref({ SEK: 1, EUR: 11.5, USD: 10.5 });

const toSEK = (amount, currency = "SEK") => {
  const rate = fxRates.value[currency] ?? fxRates.value["SEK"];
  return (parseFloat(amount) || 0) * rate;
};

const fetchDisplaySettings = async () => {
  try {
    const snap = await getDoc(doc(db, "auctionSettings", "displaySettings"));
    if (snap.exists() && snap.data().showTotalRaised !== undefined) {
      showTotalRaised.value = snap.data().showTotalRaised;
    }
  } catch {
    // keep default (visible)
  }
};

const fetchExchangeRates = async () => {
  try {
    const res = await fetch("https://api.frankfurter.app/latest?from=SEK&to=EUR,USD");
    if (!res.ok) return;
    const data = await res.json();
    // frankfurter returns rates FROM SEK, so invert them to get SEK per unit
    fxRates.value = {
      SEK: 1,
      EUR: 1 / data.rates.EUR,
      USD: 1 / data.rates.USD,
    };
  } catch {
    // keep fallback rates
  }
};

// Ring geometry
const circumference = 2 * Math.PI * 52;
const ringOffset = computed(() =>
  circumference - (displayedPercentage.value / 100) * circumference
);

// Animate percentage counter
let percentTimer = null;
watch(bidPercentage, (target) => {
  clearInterval(percentTimer);
  const start = displayedPercentage.value;
  const end = parseFloat(target) || 0;
  const steps = 60;
  let step = 0;
  percentTimer = setInterval(() => {
    step++;
    displayedPercentage.value = Math.round(start + ((end - start) * step) / steps);
    if (step >= steps) {
      displayedPercentage.value = Math.round(end);
      clearInterval(percentTimer);
    }
  }, 20);
});

// Total raised formatting
const formattedTotal = computed(() => {
  return new Intl.NumberFormat("sv-SE").format(Math.round(totalAmount.value)) + " SEK";
});

// Sorted leaderboards
const sortedTopTables = computed(() =>
  [...topTables.value].sort((a, b) => b.bidCount - a.bidCount)
);
const sortedLeadingTables = computed(() =>
  [...leadingTables.value].sort((a, b) => b.leadingBids - a.leadingBids)
);
const maxBidCount = computed(() => sortedTopTables.value[0]?.bidCount || 1);
const maxLeadingBids = computed(() => sortedLeadingTables.value[0]?.leadingBids || 1);

const barWidth = (val, max) => `${Math.max(4, Math.round((val / max) * 100))}%`;
const rankEmoji = (i) => ["🥇", "🥈", "🥉", "4", "5"][i] || String(i + 1);

// Fetch total raised — converts all currencies to SEK
const fetchTotalAmount = () => {
  onSnapshot(collection(db, "auctionItems"), async (snap) => {
    let total = 0;
    let withBids = 0;
    let bidCount = 0;
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    totalItems.value = items.filter((i) => !i.isSponsorDefault).length;

    for (const item of items) {
      if (item.isSponsorDefault) continue;
      if (item.latestBid) {
        total += toSEK(item.latestBid, item.currency);
        withBids++;
      }
      if (item.bidCount) bidCount += item.bidCount;
    }

    // Add donation purchases (each purchase may have its own currency)
    const donationItems = items.filter((i) => i.isDonationItem);
    await Promise.all(
      donationItems.map(
        (item) =>
          new Promise((resolve) => {
            onSnapshot(collection(db, `auctionItems/${item.id}/purchase`), (pSnap) => {
              const pTotal = pSnap.docs.reduce((s, d) => {
                const p = d.data();
                return s + toSEK(p.userBid, p.currency);
              }, 0);
              total += pTotal;
              resolve();
            });
          })
      )
    );

    totalAmount.value = total;
    itemsWithBids.value = withBids;
    totalBidCount.value = bidCount;
  });
};

// Particle styles
const particleStyle = (n) => ({
  left: `${(n * 37 + 11) % 100}%`,
  top: `${(n * 53 + 7) % 100}%`,
  animationDelay: `${(n * 0.7) % 5}s`,
  animationDuration: `${4 + (n % 4)}s`,
  width: `${2 + (n % 3)}px`,
  height: `${2 + (n % 3)}px`,
  opacity: 0.3 + (n % 5) * 0.1,
});

let unsubPercentage, unsubTopTables, unsubLeadingTables;

onMounted(async () => {
  setTimeout(() => (mounted.value = true), 100);

  await Promise.all([fetchDisplaySettings(), fetchExchangeRates()]);

  unsubPercentage = listenToBidPercentage((p) => (bidPercentage.value = p));
  unsubTopTables = listenToBidsPerTable((t) => (topTables.value = t));
  unsubLeadingTables = listenToTablesWithLeadingBids((t) => (leadingTables.value = t));
  fetchTotalAmount();
});

onUnmounted(() => {
  clearInterval(percentTimer);
  unsubPercentage?.();
  unsubTopTables?.();
  unsubLeadingTables?.();
});
</script>

<style scoped>
/* ── Base ─────────────────────────────────────────────────────────── */
.summary2-wrapper {
  min-height: 100vh;
  background: #07102a;
  color: #fff;
  font-family: "Roboto", sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3vh 4vw 4vh;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
}

/* ── Particles ────────────────────────────────────────────────────── */
.particles {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}
.particle {
  position: absolute;
  border-radius: 50%;
  background: #c39935;
  animation: float linear infinite;
}
@keyframes float {
  0%   { transform: translateY(0) scale(1); opacity: 0.4; }
  50%  { transform: translateY(-30px) scale(1.4); opacity: 0.8; }
  100% { transform: translateY(0) scale(1); opacity: 0.4; }
}

/* ── Header ───────────────────────────────────────────────────────── */
.summary2-header {
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
  max-width: 1400px;
  z-index: 1;
  opacity: 0;
  transform: translateY(-20px);
  transition: opacity 0.7s ease, transform 0.7s ease;
  margin-bottom: 4vh;
}
.summary2-header.visible {
  opacity: 1;
  transform: translateY(0);
}
.header-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, #c39935, transparent);
}
.summary2-title {
  font-family: "Playfair Display", serif;
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 400;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #c39935;
  white-space: nowrap;
  text-shadow: 0 0 40px rgba(195, 153, 53, 0.5);
  margin: 0;
}

/* ── Stats row ────────────────────────────────────────────────────── */
.stats-row {
  display: flex;
  gap: 2vw;
  width: 100%;
  max-width: 1400px;
  z-index: 1;
  margin-bottom: 3vh;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s;
}
.stats-row.visible {
  opacity: 1;
  transform: translateY(0);
}

.stat-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(195, 153, 53, 0.3);
  border-radius: 16px;
  padding: 2.5vh 2vw;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(195, 153, 53, 0.15);
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Ring card */
.ring-card {
  flex: 0 0 auto;
  position: relative;
  width: clamp(180px, 16vw, 240px);
  aspect-ratio: 1;
}
.ring-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}
.ring-bg {
  fill: none;
  stroke: rgba(255,255,255,0.07);
  stroke-width: 8;
}
.ring-fill {
  fill: none;
  stroke: #c39935;
  stroke-width: 8;
  stroke-linecap: round;
  transition: stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 0 6px rgba(195,153,53,0.7));
}
.ring-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.ring-value {
  font-size: clamp(2rem, 3.5vw, 3.2rem);
  font-weight: 700;
  color: #fff;
  line-height: 1;
}
.ring-unit {
  font-size: 0.5em;
  color: #c39935;
}
.ring-label {
  font-size: clamp(0.6rem, 1vw, 0.85rem);
  color: rgba(255,255,255,0.6);
  text-align: center;
  margin-top: 4px;
  line-height: 1.4;
}

/* Total card */
.total-card {
  flex: 1.4;
  gap: 6px;
}
.total-icon {
  font-size: 1.8rem;
  color: #c39935;
  animation: pulse-icon 2s ease-in-out infinite;
}
@keyframes pulse-icon {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.2); opacity: 0.8; }
}
.total-label {
  font-family: "Playfair Display", serif;
  font-size: clamp(0.9rem, 1.4vw, 1.2rem);
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.6);
}
.total-amount {
  font-family: "Playfair Display", serif;
  font-size: clamp(2rem, 3.5vw, 4rem);
  font-weight: 700;
  color: #fff;
  text-shadow: 0 0 30px rgba(195,153,53,0.5);
  background: linear-gradient(135deg, #fff 30%, #c39935);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.1;
}
.total-sub {
  font-size: clamp(0.65rem, 0.9vw, 0.8rem);
  color: rgba(255,255,255,0.35);
  letter-spacing: 0.08em;
}

/* Numbers card */
.numbers-card {
  flex: 1;
  gap: 0;
  padding: 2vh 2vw;
}
.number-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.2vh 0;
  width: 100%;
}
.number-val {
  font-size: clamp(1.6rem, 2.5vw, 2.8rem);
  font-weight: 700;
  color: #c39935;
  line-height: 1;
}
.number-desc {
  font-size: clamp(0.6rem, 0.85vw, 0.8rem);
  color: rgba(255,255,255,0.5);
  letter-spacing: 0.06em;
  margin-top: 2px;
}
.number-divider {
  width: 60%;
  height: 1px;
  background: rgba(195,153,53,0.2);
}

/* ── Leaderboards ──────────────────────────────────────────────────── */
.leaderboards {
  display: flex;
  gap: 2vw;
  width: 100%;
  max-width: 1400px;
  z-index: 1;
  flex: 1;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.7s ease 0.4s, transform 0.7s ease 0.4s;
}
.leaderboards.visible {
  opacity: 1;
  transform: translateY(0);
}

.leaderboard-card {
  flex: 1;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(195,153,53,0.25);
  border-radius: 16px;
  padding: 2.5vh 2.5vw;
  box-shadow: 0 8px 32px rgba(0,0,0,0.4);
  backdrop-filter: blur(8px);
}

.leaderboard-title {
  font-family: "Playfair Display", serif;
  font-size: clamp(1rem, 1.6vw, 1.4rem);
  font-weight: 400;
  letter-spacing: 0.1em;
  color: #c39935;
  margin: 0 0 2.5vh;
  padding-bottom: 1.5vh;
  border-bottom: 1px solid rgba(195,153,53,0.2);
  display: flex;
  align-items: center;
  gap: 10px;
}
.lb-icon { font-style: normal; }
.lb-subtitle {
  font-family: "Roboto", sans-serif;
  font-size: clamp(0.55rem, 0.8vw, 0.75rem);
  color: rgba(255,255,255,0.35);
  letter-spacing: 0.06em;
  font-weight: 400;
  margin-left: auto;
  text-transform: none;
}

.lb-list {
  display: flex;
  flex-direction: column;
  gap: 1.4vh;
}

.lb-row {
  display: flex;
  align-items: center;
  gap: 12px;
  animation: slide-in 0.5s ease both;
}
@keyframes slide-in {
  from { opacity: 0; transform: translateX(-16px); }
  to   { opacity: 1; transform: translateX(0); }
}

.lb-rank {
  width: 28px;
  font-size: 1.2rem;
  text-align: center;
  flex-shrink: 0;
}
.rank-4, .rank-5 {
  font-size: 0.9rem;
  color: rgba(255,255,255,0.4);
  font-weight: 600;
}

.lb-name {
  width: clamp(70px, 8vw, 120px);
  font-size: clamp(0.8rem, 1.1vw, 1rem);
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
}

.lb-bar-wrap {
  flex: 1;
  height: 8px;
  background: rgba(255,255,255,0.07);
  border-radius: 4px;
  overflow: hidden;
}
.lb-bar {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, #1b6ca8, #4db8ff);
  box-shadow: 0 0 8px rgba(77,184,255,0.5);
  transition: width 1s cubic-bezier(0.4, 0, 0.2, 1);
}
.lb-bar-gold {
  background: linear-gradient(90deg, #8a6b17, #c39935);
  box-shadow: 0 0 8px rgba(195,153,53,0.5);
}

.lb-count {
  width: 28px;
  text-align: right;
  font-size: clamp(0.8rem, 1.1vw, 1rem);
  font-weight: 700;
  color: rgba(255,255,255,0.7);
  flex-shrink: 0;
}
</style>
